<template>
  <div>
    <teleport to="#mapEnlargedTarget" :disabled="!fullScreen">
      <div class="relative" :class="{ fullScreen }" @keydown.esc.capture="closeFullScreen">
        <button
          ref="buttonRef"
          class="absolute right-0 z-50 p-3 m-3 bg-white rounded-full hover:bg-light-gray"
          @click="toggleFullScreen"
        >
          <Expand class="w-8 h-8" />
        </button>
        <div class="absolute left-0 z-50 flex items-center space-x-3 p-3">
          <button
            class="p-3 bg-white rounded-full hover:bg-light-gray"
            @click="resetMap"
          >
            🔄 Reset
          </button>
          <button
            class="p-3 bg-white rounded-full hover:bg-light-gray"
            @click="toggleCurrentPosition"
          >
            {{ internalVisibilityMapState.showCurrentPosition ? 'Hide' : 'Show' }} Current Position
          </button>
          <button
            v-if="visibilityMapState.currentPosition"
            class="p-3 bg-white rounded-full hover:bg-light-gray"
            @click="centerOnCurrentPosition"
          >
            Center on Current Position
          </button>
        </div>

        <!-- Stack of Locations with Drag-and-Drop -->
        <div
          class="absolute left-4 top-20 z-50 bg-white p-3 rounded-lg shadow-md transition-all duration-300"
          :class="minimized ? 'w-30 h-12' : 'w-100'"
        >
          <span class="font-bold mb-2">
            Route ({{ visibilityMapState.locationStack.length }})
          </span>
          <button class="text-blue-500 text-lg" @click="minimized = !minimized">
            {{ minimized ? '▼' : '▲' }}
          </button>
          <div v-if="!minimized" class="mt-2 transition-all duration-300 p-4">
            <draggable
              v-model="internalVisibilityMapState.locationStack"
              item-key="id-draggable"
              class="space-y-2"
            >
              <template #item="{ element, index }">
                <div
                  class="p-2 bg-gray-200 rounded-md flex items-center justify-between cursor-move"
                >
                  <span class="text-lg font-medium flex gap-2">
                    <span class="text-gray-600">{{ index + 1 }}:</span>
                    <span>{{ element.name }}</span>
                  </span>
                  <div class="flex space-x-4">
                    <button
                      class="p-2 text-blue-500 hover:text-blue-700 transition"
                      @click="removeLocation(element)"
                    >
                      ⌫
                    </button>
                    <button
                      class="p-2 text-blue-500 hover:text-blue-700 transition"
                      @click="selectLocation(element)"
                    >
                      ÷
                    </button>
                  </div>
                </div>
              </template>

            </draggable>
            <div class="flex space-x-4 ml-auto pt-2">
              <button class="text-blue-300 text-lg" @click="generateGoogleMapsLinkHandler()">
                Route generieren
              </button>
            </div>
          </div>
        </div>
        <slot
          v-bind="{ fullScreen, openFullScreen, closeFullScreen, toggleFullScreen, buttonRef }"
        />
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import Expand from '@images/expand.svg?component';
import draggable from 'vuedraggable';
import Scroll from '@/utils/scrolllock';
import type {
  StackMapLocation,
  VisibilityMapState,
  VisibilityTransferMapData,
} from '@/entities/Entities';
import { defaultCenter } from '@/utils/map';

const props = defineProps<{
  visibilityMapState: VisibilityMapState;
  points?: VisibilityTransferMapData[];
  visibilityMapRef: null,
}>();

const points = computed(() => props.points?.filter((point) => point.gisLocNew));
const fullScreen = ref<boolean>(false);
const buttonRef = ref<HTMLElement | null>(null);
const minimized = ref<boolean>(false);

const emit = defineEmits(['update:locationStack', 'update:visibilityMapState', 'update:visibilityMapRef']);
const internalVisibilityMapState = computed({
  get: () => props.visibilityMapState,
  set: (newValue) => emit('update:visibilityMapState', newValue),
});

const internalVisibilityMapRef = computed({
  get: () => props.visibilityMapRef,
  set: (newValue) => emit('update:visibilityMapRef', newValue),
});

const openFullScreen = () => {
  Scroll.lock();
  fullScreen.value = true;
  nextTick(() => {
    buttonRef.value?.focus();
  });
};
const closeFullScreen = () => {
  fullScreen.value = false;
  Scroll.unlock();
};
const toggleFullScreen = () => {
  if (fullScreen.value) {
    closeFullScreen();
  } else {
    openFullScreen();
  }
};

const LAT_PER_METER = 1.0 / 111000;
const LONG_PER_METER = 1.0 / 76000;

const computeCenter = () => {
  if (points.value?.length) {
    // eslint-disable-next-line no-undef
    const bounds = new google.maps.LatLngBounds();
    for (const point of points.value) {
      if (point.gisLocNew) {
        bounds.extend({
          lat: point.gisLocNew.lat + LAT_PER_METER * point.radius,
          lng: point.gisLocNew.long + LONG_PER_METER * point.radius,
        });
        bounds.extend({
          lat: point.gisLocNew.lat - LAT_PER_METER * point.radius,
          lng: point.gisLocNew.long - LONG_PER_METER * point.radius,
        });
        bounds.extend({
          lat: point.gisLocNew.lat - LAT_PER_METER * point.radius,
          lng: point.gisLocNew.long + LONG_PER_METER * point.radius,
        });
        bounds.extend({
          lat: point.gisLocNew.lat + LAT_PER_METER * point.radius,
          lng: point.gisLocNew.long - LONG_PER_METER * point.radius,
        });
      }

      if (point.gisLocOld) {
        bounds.extend({
          lat: point.gisLocOld.lat + LAT_PER_METER * point.radius,
          lng: point.gisLocOld.long + LONG_PER_METER * point.radius,
        });
        bounds.extend({
          lat: point.gisLocOld.lat - LAT_PER_METER * point.radius,
          lng: point.gisLocOld.long - LONG_PER_METER * point.radius,
        });
        bounds.extend({
          lat: point.gisLocOld.lat - LAT_PER_METER * point.radius,
          lng: point.gisLocOld.long + LONG_PER_METER * point.radius,
        });
        bounds.extend({
          lat: point.gisLocOld.lat + LAT_PER_METER * point.radius,
          lng: point.gisLocOld.long - LONG_PER_METER * point.radius,
        });
      }
    }
    const boundsCenter = bounds.getCenter();
    internalVisibilityMapState.value.center.value = {
      lat: boundsCenter.lat(),
      lng: boundsCenter.lng(),
    };
    internalVisibilityMapRef.value.fitBounds(bounds);
  } else {
    internalVisibilityMapState.value.center.value = defaultCenter;
  }
};

const resetMap = () => {
  computeCenter();
};

const generateGoogleMapsLinkHandler = () => {
  if (internalVisibilityMapState.value.locationStack.length === 0) {
    return '';
  }
  const baseUrl = 'https://www.google.com/maps/dir/';
  const waypoints = internalVisibilityMapState.value.locationStack.map((loc) => `${loc.lat},${loc.lng}`).join('/');
  const url = `${baseUrl}${waypoints}`;

  window.open(url, '_blank');

  return url;
};

const setCurrentPosition = () => {
  if (!navigator.geolocation) {
    console.warn('Geolocation is not supported by this browser.');
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position) => {
      internalVisibilityMapState.value.currentPosition = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      console.log('Current position set:', internalVisibilityMapState.value.currentPosition);
    },
    (error) => {
      console.error('Error getting location:', error.message);
    },
  );
};

const centerOnCurrentPosition = () => {
  if (internalVisibilityMapState.value.currentPosition) {
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        internalVisibilityMapState.value.currentPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        // internalVisibilityMapState.value.center = internalVisibilityMapState.value.currentPosition;
        internalVisibilityMapRef.value.panTo({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        console.error('Error getting location:', error.message);
      },
    );
  }
};

const toggleCurrentPosition = () => {
  if (!internalVisibilityMapState.value.showCurrentPosition) {
    setCurrentPosition();
  }
  internalVisibilityMapState.value.showCurrentPosition = !internalVisibilityMapState.value.showCurrentPosition;
};

const removeLocation = (location: StackMapLocation) => {
  if (internalVisibilityMapState.value.locationStack.some((loc) => loc.id === location.id)) {
    internalVisibilityMapState.value.locationStack.splice(internalVisibilityMapState.value.locationStack.findIndex((ls) => ls.id === location.id), 1);
  }
};

const selectLocation = (location: StackMapLocation) => {
  internalVisibilityMapRef.value.panTo({
    lat: location.lat,
    lng: location.lng,
  });
};
</script>

<style lang="scss" scoped>
.fullScreen {
  height: 100vh;
  width: 100vw;
  position: fixed;
  z-index: 9999;
}
</style>
